/**
 * Project / all SEASON SUMMARY: one event, or every event in the year.
 * Drill-down: click a race square → select that date and team, then open the day's default report.
 */
import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
  untrack,
} from "solid-js";
import * as d3 from "d3";
import DropDownButton from "../../../../../components/buttons/DropDownButton";
import Loading from "../../../../../components/utilities/Loading";
import SectionHead from "../../../../../components/reports/raceSummary/SectionHead";
import RaceDriversPanel from "../../../../../components/reports/raceSummary/RaceDriversPanel";
import { eventGapDriversView } from "../../../../../components/reports/raceSummary/raceDrivers";
import { persistantStore } from "../../../../../store/persistantStore";
import { sourcesStore } from "../../../../../store/sourcesStore";
import { setPendingRaceSummaryRace } from "../../../helpers/raceSummarySelection";
import { setSidebarState, setSidebarMenuRefreshTrigger, sidebarMenuRefreshTrigger } from "../../../../../store/globalStore";
import { clearAllFilters } from "../../../../../store/filterStore";
import {
  setSelectedEvents,
  setSelectedRanges,
  setSelectedRange,
  setCutEvents,
  setHasSelection,
  setIsCut,
} from "../../../../../store/selectionStore";
import { setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad } from "../../../../../utils/logging";
import { debug as logDebug, error as logError } from "../../../../../utils/console";
import {
  fetchEventRaceSummary,
  fetchEventRaceSummaryEvents,
  rankedEventSummary,
  mergeSeasonSummaries,
  finishGapStats,
  type EventNameOption,
  type EventRaceSummaryPayload,
  type EventRaceSummaryTeam,
} from "../../../helpers/eventRaceSummaryApi";
import "../../../../../styles/RaceSummary.css";

const {
  selectedClassName,
  selectedProjectId,
  setSelectedDate,
  setSelectedDatasetId,
  setSelectedMenu,
  setSelectedPage,
  setSelectedSourceId,
  setSelectedSourceName,
} = persistantStore;

function fmt(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toFixed(digits);
}

function fmtInt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return String(Math.round(Number(n)));
}

const RACE_TICK_MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function dayHeading(dateYmd: string): string {
  const ymd = String(dateYmd || "").replace(/\D/g, "");
  if (ymd.length < 8) return String(dateYmd || "");
  const day = Number(ymd.slice(6, 8));
  const month = RACE_TICK_MONTHS[Number(ymd.slice(4, 6))] || "";
  return month ? `${day} ${month}` : String(dateYmd || "");
}

/** One heading per calendar day, spanning that day's race columns. */
function dayGroups(races: { date_ymd: string }[]): { label: string; start: number; count: number }[] {
  const groups: { label: string; start: number; count: number }[] = [];
  races.forEach((race, index) => {
    const label = dayHeading(race.date_ymd);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.count += 1;
    else groups.push({ label, start: index, count: 1 });
  });
  return groups;
}

function deltaVs(value: number | null | undefined, bench: number | null | undefined): string {
  if (value == null || bench == null || !Number.isFinite(value) || !Number.isFinite(bench)) return "";
  const d = value - bench;
  if (Math.abs(d) < 1e-9) return "";
  const prefix = d > 0 ? "+" : "";
  return `${prefix}${d.toFixed(1)}`;
}

const ALL_EVENTS = "All";
const UNAVAILABLE = "Data currently unavailable";

/**
 * Survives leaving the page (heatmap drill-down to a day, then back). The component
 * itself remounts; without this it would initialise on the first event again.
 */
let rememberedEvent = ALL_EVENTS;
let rememberedYear = "All";
let rememberedEvents: EventNameOption[] = [];
let rememberedClassKey = "";

/** Session cache so returning to All (or any selection already loaded) does not refetch. */
const summaryCache = new Map<string, EventRaceSummaryPayload>();

const STANDING_COLS: { key: keyof EventRaceSummaryTeam; header: string; digits?: number }[] = [
  { key: "mean_position", header: "MEAN POS", digits: 2 },
  { key: "mean_start_rank", header: "START RANK", digits: 2 },
  { key: "std_position", header: "PLACE σ", digits: 2 },
  { key: "podium_perc", header: "PODIUM [%]", digits: 0 },
  { key: "races_finished", header: "FINISHED", digits: 0 },
  { key: "mean_time_behind_leader_sec", header: "BEHIND [S]", digits: 0 },
  { key: "mean_inphase_perc", header: "IN PHASE [%]", digits: 0 },
  { key: "marks_loss_per_rounding_m", header: "MARKS / RND [M]", digits: 0 },
  { key: "mnvr_loss_per_tg_m", header: "MNVR / T+G [M]", digits: 0 },
];

export default function EventRaceSummary() {
  const initialClassKey = `${selectedClassName()}\0${selectedProjectId()}`;
  const restoreEvents = rememberedClassKey === initialClassKey && rememberedEvents.length > 0;
  const [loading, setLoading] = createSignal(!restoreEvents);
  const [summaryLoading, setSummaryLoading] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [eventsReady, setEventsReady] = createSignal(restoreEvents);
  const [eventOptions, setEventOptions] = createSignal<EventNameOption[]>(
    restoreEvents ? rememberedEvents : []
  );
  const [selectedEvent, setSelectedEvent] = createSignal(rememberedEvent);
  const [selectedYear, setSelectedYear] = createSignal(rememberedYear);
  const [payload, setPayload] = createSignal<EventRaceSummaryPayload | null>(null);

  const [scatterEl, setScatterEl] = createSignal<SVGSVGElement | undefined>();
  const [heatEl, setHeatEl] = createSignal<SVGSVGElement | undefined>();
  let abort: AbortController | null = null;

  const eventNames = createMemo(() => {
    const names = [...new Set(eventOptions().map((e) => e.event_name))].sort();
    return names.length ? [ALL_EVENTS, ...names] : names;
  });

  const yearOptions = createMemo(() => {
    const ev = selectedEvent();
    const pool =
      !ev || ev === ALL_EVENTS ? eventOptions() : eventOptions().filter((e) => e.event_name === ev);
    const years = pool.filter((e) => e.year_name).map((e) => String(e.year_name));
    const uniq = [...new Set(years)].sort();
    return uniq.length ? ["All", ...uniq] : ["All"];
  });

  const driversView = createMemo(() => {
    const teams = payload()?.teams ?? [];
    return eventGapDriversView(teams);
  });

  /**
   * Primitive key. selectedClassName() reads the class object, so an effect that calls it
   * re-runs whenever that object is replaced, even when the name is unchanged.
   */
  const classProjectKey = createMemo(() => `${selectedClassName()}\0${selectedProjectId()}`);

  const eventListKey = createMemo(() =>
    eventOptions()
      .map((option) => `${option.event_name}\0${option.year_name ?? ""}`)
      .sort()
      .join("\n")
  );

  const [loadedForKey, setLoadedForKey] = createSignal(restoreEvents ? rememberedClassKey : "");
  let eventsGen = 0;

  const loadEvents = async () => {
    const gen = ++eventsGen;
    const className = untrack(selectedClassName);
    const projectId = untrack(selectedProjectId);
    if (!className || !projectId) {
      if (gen !== eventsGen) return;
      batch(() => {
        setEventOptions([]);
        setLoadedForKey("");
        setEventsReady(true);
        setLoading(false);
      });
      return;
    }
    const events = await fetchEventRaceSummaryEvents(className, projectId);
    if (gen !== eventsGen) return;
    const names = new Set(events.map((option) => option.event_name));
    const chosen = untrack(selectedEvent);
    batch(() => {
      setEventOptions(events);
      if (chosen !== ALL_EVENTS && !names.has(chosen)) {
        rememberedEvent = ALL_EVENTS;
        rememberedYear = "All";
        setSelectedEvent(ALL_EVENTS);
        setSelectedYear("All");
      }
      setLoadedForKey(`${className}\0${projectId}`);
      setEventsReady(true);
      setLoading(false);
      rememberedEvents = events;
      rememberedClassKey = `${className}\0${projectId}`;
    });
  };

  let summaryGen = 0;

  const loadSummary = async () => {
    const className = untrack(selectedClassName);
    const projectId = untrack(selectedProjectId);
    const event = untrack(selectedEvent);
    const yearSel = untrack(selectedYear);
    const listKey = untrack(eventListKey);
    if (!className || !projectId || !event) {
      setPayload(null);
      setError(null);
      return;
    }
    const key = `${className}\0${projectId}\0${event}\0${yearSel}\0${listKey}`;
    const cached = summaryCache.get(key);
    const gen = ++summaryGen;
    abort?.abort();
    if (cached) {
      abort = null;
      setError(null);
      setPayload(cached);
      setSummaryLoading(false);
      return;
    }
    const controller = new AbortController();
    abort = controller;
    setSummaryLoading(true);
    setError(null);
    try {
      const year = yearSel === "All" ? null : yearSel;
      const options = untrack(eventOptions);
      const names =
        event === ALL_EVENTS
          ? [
              ...new Set(
                options
                  .filter((option) => !year || String(option.year_name) === year)
                  .map((option) => option.event_name)
              ),
            ]
          : [event];
      const fetched = await Promise.all(
        names.map((name) => fetchEventRaceSummary(className, projectId, name, year, controller.signal))
      );
      if (gen !== summaryGen || controller.signal.aborted) return;
      const present = fetched.filter((item): item is EventRaceSummaryPayload => item != null);
      const data =
        event === ALL_EVENTS
          ? mergeSeasonSummaries(present, year)
          : present[0]
            ? rankedEventSummary(present[0])
            : null;
      if (data) summaryCache.set(key, data);
      setPayload(data);
      setError(data ? null : UNAVAILABLE);
    } catch (err) {
      if (gen !== summaryGen || controller.signal.aborted) return;
      if ((err as { name?: string })?.name === "AbortError") return;
      logError("EventRaceSummary: load failed", err);
      setError("Failed to load season summary.");
      setPayload(null);
    } finally {
      if (gen === summaryGen) setSummaryLoading(false);
    }
  };

  /** Same path as picking a date on Datasets: day mode, menu cleared so the default report opens. */
  const openDayReport = (dateYmd: string, teamName: string, raceKey: string) => {
    if (!dateYmd || dateYmd.length !== 8) return;
    const iso = `${dateYmd.slice(0, 4)}-${dateYmd.slice(4, 6)}-${dateYmd.slice(6, 8)}`;
    const sourceId = sourcesStore.getSourceId(teamName);
    setPendingRaceSummaryRace(raceKey);
    logDebug("EventRaceSummary: open day report", { iso, teamName, sourceId, raceKey });
    batch(() => {
      if (sourceId != null && sourceId > 0) {
        setSelectedSourceId(sourceId);
        setSelectedSourceName(teamName);
      }
      setSelectedDatasetId(0);
      setSelectedDate(iso);
      setSelectedMenu("");
      setSelectedPage("");
      setSidebarState("dataset");
    });
    try { setSelectedEvents([]); } catch { /* ignore */ }
    try { setSelectedRanges([]); } catch { /* ignore */ }
    try { setSelectedRange([]); } catch { /* ignore */ }
    try { setCutEvents([]); } catch { /* ignore */ }
    try { setHasSelection(false); } catch { /* ignore */ }
    try { setIsCut(false); } catch { /* ignore */ }
    try { clearAllFilters(); } catch { /* ignore */ }
    setSidebarMenuRefreshTrigger((sidebarMenuRefreshTrigger() || 0) + 1);
  };

  onMount(() => {
    logPageLoad("EventRaceSummary.tsx", "Season Summary", "Loaded");
    const cleanup = setupMediaContainerScaling({
      logPrefix: "EventRaceSummary",
      scaleToWidth: true,
    });
    onCleanup(() => {
      cleanup();
      eventsGen += 1;
      summaryGen += 1;
      abort?.abort();
    });
    void loadEvents();
  });

  // Class or project changed after the first load. Defer so mount does not fetch twice.
  createEffect(
    on(
      classProjectKey,
      () => {
        summaryCache.clear();
        setLoading(true);
        void loadEvents();
      },
      { defer: true }
    )
  );

  // Only these values start a load. Reading signals inside loadSummary must stay untracked,
  // or switching back to All re-subscribes to the event list and the page loads again.
  createEffect(
    on(
      () => [selectedEvent(), selectedYear(), eventListKey(), loadedForKey(), eventsReady()] as const,
      () => {
        if (!untrack(eventsReady) || !untrack(selectedEvent)) return;
        if (untrack(loadedForKey) !== untrack(classProjectKey)) return;
        void loadSummary();
      }
    )
  );

  createEffect(() => {
    const opts = yearOptions();
    if (!opts.includes(selectedYear())) {
      rememberedYear = "All";
      setSelectedYear("All");
    }
  });

  /** Consistency scatter: mean finish gap vs its σ, on the races shown in the heatmap. */
  createEffect(() => {
    const data = payload();
    const svgEl = scatterEl();
    if (summaryLoading() || !svgEl) return;
    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();
    const byName = new Map((data?.teams ?? []).map((team) => [team.source_name, team]));
    const points = finishGapStats(data?.matrix ?? [])
      .map((point) => {
        const team = byName.get(point.source_name);
        return team ? { team, x: point.mean, y: point.std, n: point.n } : null;
      })
      .filter((point): point is { team: EventRaceSummaryTeam; x: number; y: number; n: number } => point != null);
    if (!points.length) return;

    const width = 720;
    const height = 380;
    const margin = { top: 24, right: 72, bottom: 56, left: 64 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("class", "event-race-scatter-svg");

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    const xLo = Math.min(0, d3.min(points, (d) => d.x) ?? 0);
    const xHi = Math.max(d3.max(points, (d) => d.x) ?? 1, xLo + 1);
    const yHi = Math.max(d3.max(points, (d) => d.y) ?? 1, 1);
    const x = d3.scaleLinear().domain([xLo, xHi]).range([0, innerW]).nice();
    const y = d3.scaleLinear().domain([0, yHi]).range([innerH, 0]).nice();
    const r = d3
      .scaleSqrt()
      .domain([0, d3.max(points, (d) => d.n) ?? 1])
      .range([5, 18]);

    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(6))
      .attr("class", "event-race-axis");
    g.append("g").call(d3.axisLeft(y).ticks(6)).attr("class", "event-race-axis");

    g.append("text")
      .attr("x", innerW / 2)
      .attr("y", innerH + 40)
      .attr("text-anchor", "middle")
      .attr("class", "event-race-axis-label")
      .text("Average metres behind the winner");
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerH / 2)
      .attr("y", -48)
      .attr("text-anchor", "middle")
      .attr("class", "event-race-axis-label")
      .text("Change from race to race (m)");

    g.selectAll("circle")
      .data(points)
      .join("circle")
      .attr("cx", (d) => x(d.x))
      .attr("cy", (d) => y(d.y))
      .attr("r", (d) => r(d.n))
      .attr("fill", (d) => sourcesStore.getSourceColor(d.team.source_name) || "var(--color-text-secondary)")
      .attr("opacity", 0.85)
      .attr("stroke", "var(--color-bg-card)")
      .attr("stroke-width", 1)
      .append("title")
      .text(
        (d) =>
          `${d.team.source_name}: ${d.x.toFixed(0)} m behind the winner on average, ` +
          `changes by ${d.y.toFixed(0)} m from race to race, ${d.n} races`
      );

    g.selectAll("text.event-race-scatter-name")
      .data(points)
      .join("text")
      .attr("class", "event-race-scatter-name")
      .attr("x", (d) => x(d.x) + r(d.n) + 6)
      .attr("y", (d) => y(d.y))
      .attr("dy", "0.35em")
      .text((d) => d.team.source_name)
      .attr("fill", (d) => sourcesStore.getSourceColor(d.team.source_name) || "var(--color-text-primary)");

    onCleanup(() => {
      svg.selectAll("*").remove();
    });
  });

  /** Race × team heatmap of finish position. */
  createEffect(() => {
    const data = payload();
    const svgEl = heatEl();
    if (summaryLoading() || !svgEl || !data?.matrix?.length || !data.races?.length) {
      if (svgEl) d3.select(svgEl).selectAll("*").remove();
      return;
    }
    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    const races = data.races;
    const teams = data.matrix.map((m) => m.source_name);
    const cellW = 48;
    const cellH = 26;
    const labelW = 56;
    const margin = { top: 40, right: 12, bottom: 8, left: labelW };
    const width = margin.left + margin.right + races.length * cellW;
    const height = margin.top + margin.bottom + teams.length * cellH;
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", width)
      .attr("height", height)
      .attr("class", "event-race-heat-svg");

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    const positions = data.matrix.flatMap((row) =>
      row.cells.map((c) => c.position).filter((p): p is number => p != null && Number.isFinite(p))
    );
    const maxPos = d3.max(positions) ?? 8;
    const color = d3.scaleSequential(d3.interpolateRdYlGn).domain([maxPos, 1]);

    g.selectAll("text.team")
      .data(teams)
      .join("text")
      .attr("class", "event-race-heat-label")
      .attr("x", -8)
      .attr("y", (_, i) => i * cellH + cellH / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "end")
      .text((d) => d)
      .attr("fill", (d) => sourcesStore.getSourceColor(d) || "var(--color-text-primary)");

    const groups = dayGroups(races);
    const header = g.append("g").attr("class", "event-race-heat-header");
    header
      .selectAll("line.day")
      .data(groups.filter((group) => group.start > 0))
      .join("line")
      .attr("class", "event-race-heat-day-rule")
      .attr("x1", (group) => group.start * cellW - 1)
      .attr("x2", (group) => group.start * cellW - 1)
      .attr("y1", -32)
      .attr("y2", teams.length * cellH);
    header
      .selectAll("text.day")
      .data(groups)
      .join("text")
      .attr("class", "event-race-heat-day")
      .attr("text-anchor", "middle")
      .attr("x", (group) => (group.start + group.count / 2) * cellW)
      .attr("y", -20)
      .text((group) => group.label);
    header
      .selectAll("text.race")
      .data(races)
      .join("text")
      .attr("class", "event-race-heat-label")
      .attr("text-anchor", "middle")
      .attr("x", (_, index) => index * cellW + cellW / 2)
      .attr("y", -4)
      .text((_, index) => {
        const group = groups.find((item) => index >= item.start && index < item.start + item.count);
        return String(group ? index - group.start + 1 : index + 1);
      });

    const cells = data.matrix.flatMap((row, ti) =>
      row.cells.map((c, ri) => ({ ...c, team: row.source_name, ti, ri }))
    );

    g.selectAll("rect")
      .data(cells)
      .join("rect")
      .attr("x", (d) => d.ri * cellW)
      .attr("y", (d) => d.ti * cellH)
      .attr("width", cellW - 1)
      .attr("height", cellH - 1)
      .attr("rx", 2)
      .attr("fill", (d) =>
        d.position != null ? color(d.position) : "var(--color-bg-secondary)"
      )
      .attr("cursor", "pointer")
      .on("click", (_, d) => openDayReport(d.date_ymd, d.team, d.race_key))
      .append("title")
      .text(
        (d) =>
          `${d.team} · ${dayHeading(d.date_ymd)} race ${
            (() => {
              const group = groups.find((item) => d.ri >= item.start && d.ri < item.start + item.count);
              return group ? d.ri - group.start + 1 : d.ri + 1;
            })()
          }: pos ${d.position ?? "—"}` +
          (d.dominant_loss ? ` · loss ${d.dominant_loss}` : "")
      );

    g.selectAll("text.pos")
      .data(cells.filter((c) => c.position != null))
      .join("text")
      .attr("class", "event-race-heat-pos")
      .attr("x", (d) => d.ri * cellW + cellW / 2)
      .attr("y", (d) => d.ti * cellH + cellH / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "middle")
      .attr("pointer-events", "none")
      .text((d) => String(Math.round(d.position as number)));

    onCleanup(() => {
      svg.selectAll("*").remove();
    });
  });

  return (
    <div id="media-container" class="race-summary-page event-race-summary-page">
      <div class="container relative">
        <div class="performance-charts-scroll-container">
          <div class="performance-charts-scroll-inner event-race-summary-inner">
            <div class="race-summary-title-row">
              <h1 class="report-page-title event-race-summary-title">Season Summary</h1>
              <span class="race-summary-experimental-badge">experimental</span>
            </div>
            <p class="event-race-summary-subtitle">
              A finish gap is how many metres behind the winner a team was at the finish. Consistency
              is how much that gap changes from race to race: on the chart that change is the y-axis,
              and the average gap is the x-axis. Choose one event, or All for the season. Click a
              race square to open that race for that team.
            </p>

            <Show when={loading()}>
              <Loading />
            </Show>

            <Show when={!loading()}>
              <div class="race-summary-dropdown-section event-race-summary-controls">
                <DropDownButton
                  options={eventNames().length ? eventNames() : ["—"]}
                  defaultText={selectedEvent() || "—"}
                  smallLabel="Event"
                  size="big"
                  handleSelection={(item) => {
                    if (item === "—") return;
                    rememberedEvent = item;
                    rememberedYear = "All";
                    batch(() => {
                      setSelectedEvent(item);
                      setSelectedYear("All");
                    });
                  }}
                />
                <Show when={yearOptions().length > 1}>
                  <DropDownButton
                    options={yearOptions()}
                    defaultText={selectedYear()}
                    smallLabel="Year"
                    size="big"
                    handleSelection={(item) => {
                      rememberedYear = item;
                      setSelectedYear(item);
                    }}
                  />
                </Show>
              </div>

              <Show when={!summaryLoading() && error()}>
                <p
                  class={
                    error() === UNAVAILABLE
                      ? "race-summary-empty-message"
                      : "event-race-summary-error"
                  }
                >
                  {error()}
                </p>
              </Show>

              <Show when={summaryLoading()}>
                <Loading />
              </Show>

              <Show when={!summaryLoading() && payload()}>
                {(data) => (
                  <>
                    <Show when={data().teams.length === 0}>
                      <p class="race-summary-empty-message">{UNAVAILABLE}</p>
                    </Show>
                    <Show when={data().teams.length > 0}>
                    <div class="event-race-standings">
                    <SectionHead title="Standings" />
                    <div class="data-table-container">
                      <div class="scrollable-container">
                        <table class="maneuvers-table race-summary-table">
                          <thead>
                            <tr>
                              <th class="centered">TEAM</th>
                              <For each={STANDING_COLS}>
                                {(col) => <th class="centered">{col.header}</th>}
                              </For>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={data().teams}>
                              {(row) => {
                                const bench = data().benchmark?.best ?? {};
                                return (
                                  <tr>
                                    <td
                                      class="centered team-cell"
                                      style={{
                                        color:
                                          sourcesStore.getSourceColor(row.source_name) || undefined,
                                      }}
                                    >
                                      {row.source_name}
                                    </td>
                                    <For each={STANDING_COLS}>
                                      {(col) => {
                                        const val = row[col.key] as number | null;
                                        const digits = col.digits ?? 1;
                                        const text =
                                          col.key === "races_finished" || col.key === "podium_perc"
                                            ? fmtInt(val)
                                            : fmt(val, digits);
                                        const dText = deltaVs(val, bench[col.key as string]);
                                        return (
                                          <td class="centered">
                                            {text}
                                            <Show when={dText}>
                                              <span class="race-summary-delta">{dText}</span>
                                            </Show>
                                          </td>
                                        );
                                      }}
                                    </For>
                                  </tr>
                                );
                              }}
                            </For>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <p class="race-summary-footnote">
                      The small numbers compare each team with the best on that column. Lower is better,
                      except podium, in phase, and races finished. Start rank is the average place off the
                      line: in each start, boats are ranked by how far they travel toward mark 1 in the first
                      10 seconds — the same measure as the fleet ranking prestart chart — and those places
                      are averaged. 1 is fastest off the line. Place σ is how much finish place moves from
                      race to race: smaller is steadier. That is separate from the chart below, which is the
                      finish gap in metres.
                    </p>
                    </div>

                    <Show when={driversView()}>
                      {(view) => (
                        <div class="race-summary-drivers-block">
                          <RaceDriversPanel
                            view={view()}
                            race={null}
                            title="Where the finish gap comes from"
                          />
                          <p class="race-summary-footnote">
                            Each bar is that team&apos;s average finish gap, split into the start, maneuvers,
                            mark roundings, which side of the course they sailed, and straight-line speed.
                            The team with the smallest gap is the reference and reads zero.
                          </p>
                        </div>
                      )}
                    </Show>

                    <div class="event-race-consistency">
                      <SectionHead title="Finish gap and consistency" />
                      <div class="event-race-chart-frame">
                        <svg ref={setScatterEl} class="event-race-scatter-svg" />
                      </div>
                      <p class="race-summary-footnote">
                        Each dot is a team, on the same races as Race by race. The x-axis is their average
                        finish gap: metres behind the winner. The y-axis is how much that gap changes from
                        race to race. A small value on both axes means close to the winner and steady. A
                        larger y-axis value means the gap swings — sometimes close, sometimes far behind.
                        Bigger dots finished more races. A team with only one finish is left off, because
                        one race has no change to show.
                      </p>
                    </div>

                    <SectionHead title="Race by race" />
                    <div class="event-race-chart-frame">
                      <svg ref={setHeatEl} class="event-race-heat-svg" />
                    </div>
                    <p class="race-summary-footnote">
                      Colour is finish place (green is 1st). Click a square to open that race for that team.
                    </p>
                    </Show>
                  </>
                )}
              </Show>

              <Show when={!summaryLoading() && !payload() && !error() && selectedEvent()}>
                <p class="race-summary-empty-message">{UNAVAILABLE}</p>
              </Show>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
}
